import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { SupplyAndDemandStrings } from "@/components/tools/supply-and-demand-calculator";
import { t } from "@/lib/i18n/interpolate";
import { minus } from "@/lib/tool-kit/econ-micro/format";
import type {
  Curves,
  Equilibrium,
  Intervention,
} from "@/lib/tool-kit/econ-micro/market";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
export type CurveForm = "quantity" | "price";

export interface RawCurves {
  a: number;
  b: number;
  c: number;
  d: number;
}

interface ViewProps {
  curves: Curves;
  market: Equilibrium;
  after: Intervention | null;
  form: CurveForm;
  raw: RawCurves;
  strings: SupplyAndDemandStrings;
  fmt: Fmt;
}

/** `constant + slope × variable`, written the way a textbook does: 120 − 2P. */
export function linearText(
  constant: number,
  slope: number,
  variable: string,
  fmt: Fmt
): string {
  const number = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  if (slope === 0) {
    return number(constant);
  }
  const sign = slope < 0 ? "−" : "+";
  const size = Math.abs(slope);
  const coefficient = size === 1 ? "" : number(size);
  if (constant === 0) {
    return `${slope < 0 ? "−" : ""}${coefficient}${variable}`;
  }
  return `${number(constant)} ${sign} ${coefficient}${variable}`;
}

function SolvingLines({
  curves,
  market,
  form,
  raw,
  strings: s,
  fmt,
}: ViewProps) {
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  // A negative number inside a bracket keeps its sign visible: (120 − (−30)).
  const p = (v: number) => (v < 0 ? `(${n(v)})` : n(v));
  const isQuantity = form === "quantity";
  const variable = isQuantity ? "P" : "Q";
  const demand = linearText(raw.a, -raw.b, variable, fmt);
  const supply = linearText(raw.c, raw.d, variable, fmt);
  const solved = isQuantity ? market.price : market.quantity;
  const other = isQuantity ? market.quantity : market.price;
  const common = {
    a: n(raw.a),
    b: n(raw.b),
    c: p(raw.c),
    d: n(raw.d),
  };
  return (
    <ul className="space-y-1 font-mono text-foreground text-sm">
      <li>
        {t(isQuantity ? s.workEquateQuantity : s.workEquatePrice, {
          demand,
          supply,
        })}
      </li>
      <li>
        {t(isQuantity ? s.workSolvePrice : s.workSolveQuantity, {
          ...common,
          result: n(solved),
        })}
      </li>
      <li>
        {t(isQuantity ? s.workQuantityFromPrice : s.workPriceFromQuantity, {
          a: common.a,
          b: common.b,
          solved: n(solved),
          result: n(other),
        })}
      </li>
      <li>
        {t(s.workIntercepts, {
          alpha: n(curves.demandIntercept),
          gamma: n(curves.supplyIntercept),
        })}
      </li>
      <li>
        {t(s.workConsumerSurplus, {
          alpha: n(curves.demandIntercept),
          p: n(market.price),
          q: n(market.quantity),
          cs: n(market.consumerSurplus),
        })}
      </li>
      <li>
        {t(s.workProducerSurplus, {
          gamma: p(curves.supplyIntercept),
          p: n(market.price),
          q: n(market.quantity),
          ps: n(market.producerSurplus),
        })}
      </li>
    </ul>
  );
}

function InterventionLines({
  market,
  after,
  form,
  raw,
  strings: s,
  fmt,
}: ViewProps & { after: Intervention }) {
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  const tax = after.perUnit > 0;
  // A subsidy is a negative tax: keep its sign visible inside the formula.
  const signedT =
    after.perUnit < 0 ? `(${n(after.perUnit)})` : n(after.perUnit);
  const size = Math.abs(after.perUnit);
  const shift = Math.abs(market.quantity - after.quantity);
  const quantityLine =
    form === "quantity"
      ? t(s.workQuantityQuantityForm, {
          qstar: n(market.quantity),
          t: signedT,
          b: n(raw.b),
          d: n(raw.d),
          result: n(after.quantity),
        })
      : t(s.workQuantityPriceForm, {
          qstar: n(market.quantity),
          t: signedT,
          b: n(raw.b),
          d: n(raw.d),
          result: n(after.quantity),
        });
  const buyerChange = after.buyerPrice - market.price;
  const sellerChange = after.sellerPrice - market.price;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{tax ? s.taxHeading : s.subsidyHeading}</h3>
      <p className="text-foreground text-sm">
        {t(tax ? s.wedgeTax : s.wedgeSubsidy, { t: n(size) })}
      </p>
      <ul className="space-y-1 font-mono text-foreground text-sm">
        <li>{quantityLine}</li>
        <li>
          {t(s.workBuyerPrice, {
            q: n(after.quantity),
            pb: n(after.buyerPrice),
          })}
        </li>
        <li>
          {t(s.workSellerPrice, {
            pb: n(after.buyerPrice),
            t: signedT,
            ps: n(after.sellerPrice),
          })}
        </li>
        <li>
          {t(tax ? s.workRevenueTax : s.workRevenueSubsidy, {
            t: n(size),
            q: n(after.quantity),
            result: n(Math.abs(after.governmentRevenue)),
          })}
        </li>
        <li>
          {t(s.workLoss, {
            t: n(size),
            dq: n(shift),
            result: n(after.deadweightLoss),
          })}
        </li>
      </ul>
      <p className="text-foreground text-sm">
        {t(tax ? s.incidenceTax : s.incidenceSubsidy, {
          buyers: n(Math.abs(buyerChange)),
          buyerShare: fmt(after.buyerShare, {
            style: "percent",
            maximumFractionDigits: 1,
          }),
          sellers: n(Math.abs(sellerChange)),
          sellerShare: fmt(after.sellerShare, {
            style: "percent",
            maximumFractionDigits: 1,
          }),
          buyerSide: buyerChange > 0 ? s.higher : s.lower,
          sellerSide: sellerChange > 0 ? s.higher : s.lower,
        })}
      </p>
      <p className="text-muted-foreground text-sm">
        {t(tax ? s.incidenceRuleTax : s.incidenceRuleSubsidy, {
          es: fmt(market.supplyElasticity, { maximumFractionDigits: 3 }),
          ed: fmt(market.demandElasticity, { maximumFractionDigits: 3 }),
        })}
      </p>
    </div>
  );
}

function ComparisonTable({
  market,
  after,
  strings: s,
  fmt,
}: Pick<ViewProps, "market" | "after" | "strings" | "fmt">) {
  if (!after) {
    return null;
  }
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 2 }));
  const signed = (v: number) => (v > 0 ? `+${n(v)}` : n(v));
  const row = (label: string, before: number, now: number) => [
    label,
    n(before),
    n(now),
    signed(now - before),
  ];
  const tax = after.perUnit > 0;
  return (
    <DataTable
      head={[s.tableMeasure, s.tableBefore, s.tableAfter, s.tableChange]}
      rows={[
        row(s.tableBuyerPrice, market.price, after.buyerPrice),
        row(s.tableSellerPrice, market.price, after.sellerPrice),
        row(s.tableQuantity, market.quantity, after.quantity),
        row(
          s.statConsumerSurplus,
          market.consumerSurplus,
          after.consumerSurplus
        ),
        row(
          s.statProducerSurplus,
          market.producerSurplus,
          after.producerSurplus
        ),
        row(
          tax ? s.tableRevenue : s.tableSubsidyBill,
          0,
          tax ? after.governmentRevenue : -after.governmentRevenue
        ),
        row(
          s.tableTotalWelfare,
          market.totalSurplus,
          after.consumerSurplus +
            after.producerSurplus +
            after.governmentRevenue
        ),
        [
          s.tableLoss,
          "0",
          n(after.deadweightLoss),
          `−${n(after.deadweightLoss)}`,
        ],
      ]}
    />
  );
}

export function MarketView(props: ViewProps) {
  const { curves, market, after, strings: s, fmt } = props;
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 2 }));
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard emphasis label={s.statPrice} value={n(market.price)} />
        <StatCard emphasis label={s.statQuantity} value={n(market.quantity)} />
        <StatCard
          label={s.statConsumerSurplus}
          value={n(market.consumerSurplus)}
        />
        <StatCard
          label={s.statProducerSurplus}
          value={n(market.producerSurplus)}
        />
        <StatCard label={s.statTotalSurplus} value={n(market.totalSurplus)} />
        <StatCard
          label={s.statElasticities}
          value={`${fmt(market.demandElasticity, { maximumFractionDigits: 2 })} · ${fmt(market.supplyElasticity, { maximumFractionDigits: 2 })}`}
        />
      </StatGrid>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{s.workingHeading}</h3>
        <SolvingLines {...props} />
        {curves.supplyIntercept < 0 ? (
          <p className="text-muted-foreground text-xs">
            {s.negativeInterceptNote}
          </p>
        ) : null}
      </div>
      {after ? (
        <>
          <InterventionLines {...props} after={after} />
          <ComparisonTable
            after={after}
            fmt={fmt}
            market={market}
            strings={s}
          />
        </>
      ) : null}
    </div>
  );
}
